/**
 * QuickAddModal Component
 *
 * Fast transaction entry modal - the most important UX element.
 * Designed for one-handed mobile use with minimal taps.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, AmountInput, Input, Select } from '@/components/shared';
import { db, Account, Category, TransactionType, ScheduleFrequency } from '@services/db';
import { transactionService } from '@services/transactions';
import { accountService } from '@services/accounts';
import { scheduledTransactionService } from '@services/scheduledTransactions';
import { normalizeDetectionKey } from '@services/scheduledBridge';
import { dataEvents } from '@services/dataEvents';
import { parseDateAsLocal, getTodayString, getYesterdayString } from '@/utils/date';
import { useLanguage, categoryDisplayName } from '@/i18n';
import './QuickAddModal.css';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultType?: TransactionType;
}

type FormState = {
  type: TransactionType;
  amount: string;
  accountId: string;
  categoryId: string;
  toAccountId: string;
  description: string;
  date: string;
  isRecurring: boolean;
  frequency: ScheduleFrequency;
};

function createInitialFormState(type: TransactionType = 'expense', accountId = ''): FormState {
  return {
    type,
    amount: '',
    accountId,
    categoryId: '',
    toAccountId: '',
    description: '',
    date: getTodayString(),
    isRecurring: false,
    frequency: 'monthly', // Default to monthly (most common)
  };
}

// Frequency options for recurring transactions
// Labels are catalog keys, resolved with t() at render (enums never change).
const FREQUENCY_OPTIONS: { value: ScheduleFrequency; label: string }[] = [
  { value: 'weekly', label: 'transactions.frequency.weekly' },
  { value: 'biweekly', label: 'transactions.frequency.biweekly' },
  { value: 'monthly', label: 'transactions.frequency.monthly' },
  { value: 'yearly', label: 'transactions.frequency.yearly' },
];

// Description autocomplete: one entry per distinct past description,
// carrying the category/account of its most recent use so picking a
// suggestion auto-fills both ("steak" -> Restaurant · C-app).
interface DescSuggestion {
  description: string;
  categoryId?: string;
  accountId: string;
  type: TransactionType;
  count: number;
  lastTime: number;
}

function isAdjustmentDesc(desc: string): boolean {
  const lower = desc.toLowerCase();
  return lower.includes('[adjustment]') || lower.startsWith('adjustment');
}

export function QuickAddModal({
  isOpen,
  onClose,
  onSuccess,
  defaultType = 'expense',
}: QuickAddModalProps) {
  const { t } = useLanguage();
  const [form, setForm] = useState<FormState>(() => createInitialFormState(defaultType));
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateTouchedRef = useRef(false);

  // Description autocomplete index (built once per open from recent txns)
  const suggestIndexRef = useRef<DescSuggestion[]>([]);
  const [suggestReady, setSuggestReady] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Load accounts and categories when modal opens, subscribe to changes
  // Do NOT include form.accountId - that would cause reload loops
  useEffect(() => {
    if (isOpen) {
      const loadData = async () => {
        try {
          const [accts, cats] = await Promise.all([
            accountService.getActive(),
            db.categories.filter((c) => c.isActive === true).toArray(),
          ]);
          setAccounts(accts);
          setCategories(cats);
        } catch (err) {
          // Handle NotFoundError when tables don't exist yet (schema upgrade in progress)
          if (err instanceof Error && err.name === 'NotFoundError') {
            console.warn('[QuickAdd] Tables not found - schema may be upgrading');
            return;
          }
          throw err;
        }
      };
      loadData();

      // Build the description-suggestion index from recent transactions
      (async () => {
        try {
          const recent = await transactionService.getFiltered({ limit: 600 });
          const map = new Map<string, DescSuggestion>();
          for (const txn of recent) {
            const desc = (txn.description || '').trim();
            if (!desc || txn.type === 'transfer' || isAdjustmentDesc(desc)) continue;
            const key = desc.toLowerCase();
            const time = new Date(txn.date).getTime();
            const existing = map.get(key);
            if (existing) {
              existing.count += 1;
              if (time > existing.lastTime) {
                existing.lastTime = time;
                existing.description = desc;
                existing.categoryId = txn.categoryId;
                existing.accountId = txn.accountId;
                existing.type = txn.type;
              }
            } else {
              map.set(key, {
                description: desc,
                categoryId: txn.categoryId,
                accountId: txn.accountId,
                type: txn.type,
                count: 1,
                lastTime: time,
              });
            }
          }
          suggestIndexRef.current = Array.from(map.values());
          setSuggestReady((n) => n + 1);
        } catch (err) {
          console.warn('[QuickAdd] Suggestion index failed:', err);
        }
      })();

      // Subscribe to data events for real-time updates while modal is open
      const unsubCategories = dataEvents.on('categories', loadData);
      const unsubAccounts = dataEvents.on('accounts', loadData);
      return () => {
        unsubCategories();
        unsubAccounts();
      };
    }
  }, [isOpen]);

  // Reset form when modal opens
  // Use a ref to track if we've initialized to avoid resetting on every accounts change
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isOpen && accounts.length > 0 && !hasInitialized.current) {
      dateTouchedRef.current = false;
      setForm(createInitialFormState(defaultType, accounts[0]?.id || ''));
      setError(null);
      hasInitialized.current = true;
    }
    // Reset the ref when modal closes
    if (!isOpen) {
      hasInitialized.current = false;
      dateTouchedRef.current = false;
    }
  }, [isOpen, defaultType, accounts]);

  // Filter categories by type, sorted with top-tier categories first
  const filteredCategories = useMemo(() => {
    if (form.type === 'transfer') return [];
    const filtered = categories.filter((c) => c.type === form.type);

    // Priority categories for expense (lower number = higher priority)
    // User-defined top-tier categories that should always appear first
    const expensePriority: Record<string, number> = {
      'grocery': 1, 'groceries': 1,
      'food': 2,
      'car': 3,
      'restaurant': 4, 'doordash': 4, 'restaurants': 4,
      'house': 5, 'home': 5, 'rent': 5,
      'business': 6,
    };

    // Priority categories for income
    const incomePriority: Record<string, number> = {
      'salary': 1,
      'freelance': 2,
      'business': 3,
    };

    const getPriority = (name: string): number => {
      const lowerName = name.toLowerCase();
      if (form.type === 'expense') {
        return expensePriority[lowerName] ?? 100;
      } else if (form.type === 'income') {
        return incomePriority[lowerName] ?? 100;
      }
      return 100;
    };

    return filtered.sort((a, b) => {
      const priorityA = getPriority(a.name);
      const priorityB = getPriority(b.name);
      // Sort by priority first, then alphabetically
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [categories, form.type]);

  // Set default category when type changes
  useEffect(() => {
    if (filteredCategories.length > 0 && !form.categoryId) {
      setForm((f) => ({ ...f, categoryId: filteredCategories[0].id! }));
    }
  }, [filteredCategories, form.categoryId]);

  // Handle form field change
  const handleChange = useCallback(
    (field: keyof FormState, value: string) => {
      if (field === 'date') {
        dateTouchedRef.current = true;
      }
      setForm((f) => ({ ...f, [field]: value }));
      setError(null);
    },
    []
  );

  // Handle type change
  const handleTypeChange = useCallback((type: TransactionType) => {
    setForm((f) => ({
      ...f,
      type,
      categoryId: '', // Reset category on type change
      toAccountId: '', // Reset transfer target
    }));
  }, []);

  // Validate form
  const validate = useCallback((): string | null => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      return t('accounts.actions.err_invalid_amount');
    }
    if (!form.accountId) {
      return t('quickadd.err_account');
    }
    if (form.type !== 'transfer' && !form.categoryId) {
      return t('quickadd.err_category');
    }
    if (form.type === 'transfer' && !form.toAccountId) {
      return t('accounts.actions.err_select_dest');
    }
    if (form.type === 'transfer' && form.accountId === form.toAccountId) {
      return t('quickadd.err_same_account');
    }
    // Validate date - ensure it's a valid date string
    if (!form.date || isNaN(new Date(form.date).getTime())) {
      return t('quickadd.err_date');
    }
    return null;
  }, [form, t]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const account = accounts.find((a) => a.id === form.accountId);
      const amount = parseFloat(form.amount);
      const effectiveDate = dateTouchedRef.current ? form.date : getTodayString();
      const transactionDate = parseDateAsLocal(effectiveDate);

      let scheduledTransactionId: string | undefined;

      // If recurring, create ScheduledTransaction first
      if (form.isRecurring && form.type !== 'transfer') {
        // Calculate next due date based on frequency
        const nextDueDate = new Date(transactionDate);
        switch (form.frequency) {
          case 'weekly':
            nextDueDate.setDate(nextDueDate.getDate() + 7);
            break;
          case 'biweekly':
            nextDueDate.setDate(nextDueDate.getDate() + 14);
            break;
          case 'monthly':
            nextDueDate.setMonth(nextDueDate.getMonth() + 1);
            break;
          case 'yearly':
            nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);
            break;
        }

        // Create the scheduled transaction
        scheduledTransactionId = await scheduledTransactionService.create({
          accountId: form.accountId,
          categoryId: form.categoryId,
          type: form.type,
          amount,
          description: form.description,
          frequency: form.frequency,
          dayOfMonth: form.frequency === 'monthly' || form.frequency === 'yearly'
            ? transactionDate.getDate()
            : undefined,
          dayOfWeek: form.frequency === 'weekly' || form.frequency === 'biweekly'
            ? transactionDate.getDay()
            : undefined,
          nextDueDate,
          lastGeneratedDate: transactionDate,
          isPaused: false,
          isIgnoredPermanently: false,
          autoDetected: false, // Manually created
          detectionKey: normalizeDetectionKey(form.description), // For matching
          dismissCount: 0,
        });

        console.log('[QuickAdd] Created scheduled transaction:', scheduledTransactionId);
      }

      // Create the transaction (linked to schedule if recurring)
      await transactionService.create({
        type: form.type,
        amount,
        accountId: form.accountId,
        categoryId: form.type !== 'transfer' ? form.categoryId : undefined,
        toAccountId: form.type === 'transfer' ? form.toAccountId : undefined,
        description: form.description,
        // CRITICAL: Parse date as LOCAL time, not UTC
        // new Date("2025-12-14") would parse as UTC midnight, shifting the date
        // backward in timezones behind UTC (like US timezones)
        date: transactionDate,
        currency: account?.currency || 'USD',
        tags: form.isRecurring ? ['recurring'] : undefined,
        scheduledTransactionId, // Link to schedule if recurring
      });

      // Balance is updated incrementally in transactionService.create()
      // No need to call recalculateAllBalances() which would overwrite starting balances

      console.log('[QuickAdd] Transaction created successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('quickadd.err_failed');
      setError(message);
      console.error('[QuickAdd] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [form, accounts, validate, onSuccess, onClose]);

  // Matching description suggestions (prefix matches first, then by
  // frequency, then recency)
  const descSuggestions = useMemo(() => {
    const q = form.description.trim().toLowerCase();
    if (q.length < 2) return [];
    return suggestIndexRef.current
      .filter((s) => {
        const lower = s.description.toLowerCase();
        return lower !== q && lower.includes(q);
      })
      .sort((a, b) => {
        const aPrefix = a.description.toLowerCase().startsWith(q) ? 0 : 1;
        const bPrefix = b.description.toLowerCase().startsWith(q) ? 0 : 1;
        if (aPrefix !== bPrefix) return aPrefix - bPrefix;
        if (b.count !== a.count) return b.count - a.count;
        return b.lastTime - a.lastTime;
      })
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.description, suggestReady]);

  // Picking a suggestion fills the description AND remembers its category/
  // account (category only when the txn type matches the current tab;
  // account only when it still exists)
  const applySuggestion = useCallback((s: DescSuggestion) => {
    setForm((f) => ({
      ...f,
      description: s.description,
      categoryId: s.type === f.type && s.categoryId ? s.categoryId : f.categoryId,
      accountId: accounts.some((a) => a.id === s.accountId) ? s.accountId : f.accountId,
    }));
    setShowSuggestions(false);
  }, [accounts]);

  // Get currency for selected account
  const selectedCurrency = useMemo(() => {
    const account = accounts.find((a) => a.id === form.accountId);
    return account?.currency || 'USD';
  }, [accounts, form.accountId]);

  // BUG-014 FIX: Detect currency mismatch for transfers
  const currencyMismatch = useMemo(() => {
    if (form.type !== 'transfer' || !form.accountId || !form.toAccountId) {
      return null;
    }
    const fromAccount = accounts.find((a) => a.id === form.accountId);
    const toAccount = accounts.find((a) => a.id === form.toAccountId);
    if (fromAccount && toAccount && fromAccount.currency !== toAccount.currency) {
      return {
        fromCurrency: fromAccount.currency,
        toCurrency: toAccount.currency,
      };
    }
    return null;
  }, [form.type, form.accountId, form.toAccountId, accounts]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('quickadd.title')} size="lg">
      <div className="quick-add-form">
        {/* Transaction Type Tabs */}
        <div className="type-tabs">
          {(['expense', 'income', 'transfer'] as TransactionType[]).map((type) => (
            <button
              key={type}
              type="button"
              className={`type-tab type-tab-${type} ${form.type === type ? 'active' : ''}`}
              onClick={() => handleTypeChange(type)}
            >
              {type === 'expense' ? '-' : type === 'income' ? '+' : '>'}
              <span>{t(`transactions.type.${type}`)}</span>
            </button>
          ))}
        </div>

        {/* Amount Input */}
        <div className="amount-section">
          <AmountInput
            currency={selectedCurrency}
            value={form.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            placeholder="0.00"
            autoFocus
          />
        </div>

        {/* Account Select - label changes based on transaction type */}
        <Select
          label={form.type === 'income' ? t('accounts.actions.to_account') : t('quickadd.from_account')}
          value={form.accountId}
          onChange={(e) => handleChange('accountId', e.target.value)}
          options={accounts.map((a) => ({
            value: a.id!,
            label: a.fullName ? `${a.name} (${a.fullName})` : a.name,
          }))}
        />

        {/* Category or Transfer Target */}
        {form.type === 'transfer' ? (
          <>
            <Select
              label={t('accounts.actions.to_account')}
              value={form.toAccountId}
              onChange={(e) => handleChange('toAccountId', e.target.value)}
              options={[
                { value: '', label: t('quickadd.select_account') },
                ...accounts
                  .filter((a) => a.id !== form.accountId)
                  .map((a) => ({
                    value: a.id!,
                    label: a.fullName ? `${a.name} (${a.fullName})` : a.name,
                  })),
              ]}
            />
            {/* BUG-014 FIX: Currency mismatch warning */}
            {currencyMismatch && (
              <div className="currency-mismatch-warning">
                {t('quickadd.currency_mismatch', {
                  from: currencyMismatch.fromCurrency,
                  to: currencyMismatch.toCurrency,
                })}
              </div>
            )}
          </>
        ) : (
          <Select
            label={t('transactions.detail.category')}
            value={form.categoryId}
            onChange={(e) => handleChange('categoryId', e.target.value)}
            options={[
              { value: '', label: t('quickadd.select_category') },
              ...filteredCategories.map((c) => ({
                value: c.id!,
                label: c.icon ? `${c.icon} ${categoryDisplayName(c.name)}` : categoryDisplayName(c.name),
              })),
            ]}
          />
        )}

        {/* Description with learn-from-history autocomplete */}
        <div className="desc-suggest-wrap">
          <Input
            label={t('quickadd.note_label')}
            value={form.description}
            onChange={(e) => {
              handleChange('description', e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => window.setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowSuggestions(false);
            }}
            placeholder={t('quickadd.note_placeholder')}
            autoComplete="off"
          />
          {showSuggestions && descSuggestions.length > 0 && (
            <div className="desc-suggestions" role="listbox" aria-label={t('quickadd.suggestions_aria')}>
              {descSuggestions.map((s) => {
                const cat = categories.find((c) => c.id === s.categoryId);
                return (
                  <button
                    key={s.description.toLowerCase()}
                    type="button"
                    className="desc-suggestion"
                    role="option"
                    aria-selected="false"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySuggestion(s)}
                  >
                    <span className="desc-suggestion-text">{s.description}</span>
                    {cat && s.type === form.type && (
                      <span className="desc-suggestion-meta">
                        {cat.icon ? `${cat.icon} ` : ''}{categoryDisplayName(cat.name)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Date + quick chips */}
        <Input
          type="date"
          label={t('transactions.detail.date')}
          value={form.date}
          onChange={(e) => handleChange('date', e.target.value)}
        />
        <div className="date-quick-chips">
          <button
            type="button"
            className={`date-quick-chip ${form.date === getTodayString() ? 'active' : ''}`}
            onClick={() => handleChange('date', getTodayString())}
          >
            {t('quickadd.today')}
          </button>
          <button
            type="button"
            className={`date-quick-chip ${form.date === getYesterdayString() ? 'active' : ''}`}
            onClick={() => handleChange('date', getYesterdayString())}
          >
            {t('quickadd.yesterday')}
          </button>
        </div>

        {/* Recurring Toggle + Frequency Selector */}
        {form.type !== 'transfer' && (
          <div className="recurring-section">
            <label className="recurring-toggle">
              <input
                type="checkbox"
                checked={form.isRecurring}
                onChange={(e) => setForm((f) => ({ ...f, isRecurring: e.target.checked }))}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">{t('transactions.detail.recurring')}</span>
            </label>

            {/* Frequency selector - shows when recurring is ON */}
            {form.isRecurring && (
              <div className="frequency-selector">
                <span className="frequency-label">{t('transactions.detail.repeats')}</span>
                <div className="frequency-options">
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`frequency-btn ${form.frequency === opt.value ? 'active' : ''}`}
                      onClick={() => setForm((f) => ({ ...f, frequency: opt.value }))}
                    >
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Submit Button */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? t('common.saving') : t('nav.add_transaction')}
        </Button>
      </div>
    </Modal>
  );
}
